// Which link and channel brought a visitor (goldie-cloud
// docs/growth/referral-attribution.md). A link into the app may carry
// ref=<referral code> (or the older referral_code=), ch=<channel> and
// utm_source / utm_medium / utm_campaign; the landing page adds lp=<the first
// page opened there>. The tags of the FIRST tagged visit are kept here for 30
// days and sent with the sign-up as `attribution`, so a visitor who looks
// around for a week before signing up is still counted against the link that
// brought them. The code itself goes through the Referrer code field as
// before (SignUp prefills it from the tags).
//
// localStorage can be unavailable (Safari private mode, a locked-down
// browser): every access is guarded, and a visitor without storage simply
// signs up untagged.

const KEY = 'goldie:attribution'
export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000
const MAX_LENGTH = 200

export interface Tags {
  ref?: string
  channel?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  /** The first page opened on the landing page (lp=), when it sent one. */
  lp?: string
}

export interface StoredAttribution extends Omit<Tags, 'lp'> {
  landing_path: string
  /** ISO 8601, when the tags were first seen. */
  first_seen_at: string
  /** Unix time in milliseconds. */
  expires_at: number
}

/** What POST /auth/signup takes as `attribution` (api_types.SignupAttributionIn). */
export interface SignupAttribution {
  channel?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  landing_path?: string
  first_seen_at?: string
}

function clean(value: string | null): string | undefined {
  const trimmed = (value ?? '').trim().slice(0, MAX_LENGTH).trim()
  return trimmed || undefined
}

/** The tags in a query string; an absent one is left out. */
export function tagsFrom(search: string): Tags {
  const params = new URLSearchParams(search)
  const tags: Tags = {
    ref: clean(params.get('ref') ?? params.get('referral_code')),
    channel: clean(params.get('ch')),
    utm_source: clean(params.get('utm_source')),
    utm_medium: clean(params.get('utm_medium')),
    utm_campaign: clean(params.get('utm_campaign')),
    lp: clean(params.get('lp'))
  }
  return Object.fromEntries(
    Object.entries(tags).filter(([, v]) => v !== undefined)
  ) as Tags
}

function hasTags(tags: Tags): boolean {
  return Boolean(
    tags.ref ||
      tags.channel ||
      tags.utm_source ||
      tags.utm_medium ||
      tags.utm_campaign
  )
}

function remove(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* no storage */
  }
}

/** The stored tags, or null when there are none or they have expired. */
export function readAttribution(now = Date.now()): StoredAttribution | null {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const stored = JSON.parse(raw) as StoredAttribution
    if (
      typeof stored !== 'object' ||
      stored === null ||
      typeof stored.expires_at !== 'number' ||
      stored.expires_at <= now
    ) {
      remove()
      return null
    }
    return stored
  } catch {
    remove()
    return null
  }
}

/**
 * Keep the tags of this page view when it carries any and nothing unexpired
 * is kept yet (the first tagged visit wins). Returns what is kept, if anything.
 */
export function captureAttribution(
  location: { pathname: string; search: string },
  now = Date.now()
): StoredAttribution | null {
  const existing = readAttribution(now)
  if (existing) return existing
  const tags = tagsFrom(location.search)
  if (!hasTags(tags)) return null
  const { lp, ...rest } = tags
  const landing = lp && lp.startsWith('/') ? lp : location.pathname || '/'
  const stored: StoredAttribution = {
    ...rest,
    landing_path: landing.slice(0, MAX_LENGTH),
    first_seen_at: new Date(now).toISOString(),
    expires_at: now + ATTRIBUTION_TTL_MS
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(stored))
  } catch {
    return null
  }
  return stored
}

/** The referral code a tagged visit carried, for the Referrer code field. */
export function storedReferralCode(): string {
  return readAttribution()?.ref ?? ''
}

/** The sign-up's `attribution` payload, or undefined when nothing is kept. */
export function signupAttribution(): SignupAttribution | undefined {
  const stored = readAttribution()
  if (!stored) return undefined
  const payload: SignupAttribution = {
    channel: stored.channel,
    utm_source: stored.utm_source,
    utm_medium: stored.utm_medium,
    utm_campaign: stored.utm_campaign,
    landing_path: stored.landing_path,
    first_seen_at: stored.first_seen_at
  }
  return Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined)
  ) as SignupAttribution
}

/** Forget the tags (after a sign-up has used them). */
export function clearAttribution(): void {
  remove()
}
